import { unreachable } from "../unreachable.js";
/**
 * Generate a search query string from a pattern and params.
 *
 * @param pattern the route pattern containing search constraints
 * @param searchParams the search params to include in the href
 * @returns the query string (without leading `?`), or undefined if empty
 */ export function hrefSearch(pattern, searchParams) {
  let constraints = pattern.ast.search;
  if (constraints.size === 0 && Object.keys(searchParams).length === 0) {
    return undefined;
  }
  let urlSearchParams = new URLSearchParams();
  for (let [key, value] of Object.entries(searchParams)){
    if (Array.isArray(value)) {
      for (let v of value){
        if (v != null) {
          urlSearchParams.append(key, String(v));
        }
      }
    } else if (value != null) {
      urlSearchParams.append(key, String(value));
    }
  }
  for (let [key, requiredValues] of constraints){
    if (requiredValues.size === 0) {
      if (key in searchParams) continue;
      urlSearchParams.append(key, '');
    } else {
      for (let value of requiredValues){
        if (urlSearchParams.getAll(key).includes(value)) continue;
        urlSearchParams.append(key, value);
      }
    }
  }
  let result = urlSearchParams.toString();
  return result || undefined;
}
/**
 * Error thrown when a route pattern cannot generate an href from the supplied args.
 */ export class HrefError extends Error {
  /**
     * Structured details describing why href generation failed.
     */ details;
  constructor(details){
    let message = HrefError.message(details);
    super(message);
    this.name = 'HrefError';
    this.details = details;
  }
  /**
     * Formats an error message for the given href failure details.
     *
     * @param details Structured href failure details.
     * @returns A human-readable error message.
     */ static message(details) {
    let pattern = details.pattern.toString();
    if (details.type === 'missing-hostname') {
      return `pattern requires hostname\n\nPattern: ${pattern}`;
    }
    if (details.type === 'nameless-wildcard') {
      return `pattern contains nameless wildcard\n\nPattern: ${pattern}`;
    }
    if (details.type === 'missing-params') {
      let params = details.missingParams.map((p)=>`'${p}'`).join(', ');
      return `missing param(s): ${params}\n\nPattern: ${pattern}\nParams: ${JSON.stringify(details.params)}`;
    }
    unreachable(details);
  }
}
